import React from 'react';
import classNames from 'classnames';

import {
  SunHaze as EarlyArrivalIcon,
  Night as LateDepartureIcon,
} from '@lt/components/icons-react/rebrand';
import PlaneIcon from '@lt/components/icons-react/rebrand/Plane';

import type { FlightsData } from 'checkout/CheckoutRoutes/PackageCheckout/types/flights';
import { getHours } from 'date-fns';
import {
  FLIGHT_ARRIVAL_EARLY_TIME_WARNING_OFFSET,
  FLIGHT_LATE_DEPARTURE_TIME_WARNING_OFFSET,
  getFlightTypeText,
} from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import {
  getDateWithoutTimezone,
  getFlightTypeData,
} from 'checkout/CheckoutRoutes/PackageCheckout/utils/flightUtils';
import { i18n } from '@lt/localization';
import LabelWithDescription from './components/LabelWithDescription';

import styles from './labelsContainer.module.css';

interface LabelsContainerProps {
  data: FlightsData['to'];
  type: 'to' | 'back';
  flightNumber?: string;
  isOrderPage?: boolean;
  isBusinessClass: boolean;
  isMobile: boolean;
  operatorID: number;
}

export const LabelsContainer = ({
  data,
  type,
  isOrderPage,
  flightNumber,
  isBusinessClass,
  operatorID,
  isMobile,
}: LabelsContainerProps): JSX.Element => {
  const { arrival, time, departure } = data;

  const parseDeparture = departure ? getDateWithoutTimezone(departure) : null;
  const parseArrival = arrival ? getDateWithoutTimezone(arrival) : null;

  // получаем данные для лейбла раннего вылета
  const showEarlyArrivingWarning =
    type === 'to' &&
    !!time &&
    arrival &&
    parseArrival &&
    getHours(new Date(parseArrival)) < FLIGHT_ARRIVAL_EARLY_TIME_WARNING_OFFSET;

  // получаем данные для лейбла позднего перелета
  const showLateDepartureWarning =
    type === 'back' &&
    departure &&
    parseDeparture &&
    getHours(new Date(parseDeparture)) >=
      FLIGHT_LATE_DEPARTURE_TIME_WARNING_OFFSET;

  // получаем данные для лебйла о типе рейса
  const { flightType } = getFlightTypeData(data);
  const flightTypeData = getFlightTypeText(flightType, { operatorID });
  const tooltipTitle = isOrderPage ? undefined : flightTypeData.tooltipTitle;
  const tooltipDescription = isOrderPage
    ? undefined
    : flightTypeData.tooltipDescription;
  const earlyTooltipTitle = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.early_arrival.tooltip_title');
  const earlyTooltipDescription = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.early_arrival.tooltip_description');
  const lateTooltipTitle = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.late_departure.tooltip_title');
  const lateTooltipDescription = isOrderPage
    ? undefined
    : i18n.t('checkout.flight.late_departure.tooltip_description');
  const flightClassText = isBusinessClass
    ? i18n.t('checkout.flight.business.name')
    : i18n.t('checkout.flight.economy.name');

  return (
    <div className={classNames(styles.root, { [styles.rootMobile]: isMobile })}>
      {flightType && (
        <LabelWithDescription
          text={flightTypeData.title}
          tooltipTitle={tooltipTitle}
          tooltipExtra={tooltipDescription}
          flightNumber={flightNumber}
          flightClassText={flightClassText}
          isMobile={isMobile}
          icon={<PlaneIcon className={styles.labelIcon} />}
        />
      )}
      {showEarlyArrivingWarning && (
        <LabelWithDescription
          text={i18n.t('checkout.flight.early_arrival.title')}
          tooltipTitle={earlyTooltipTitle}
          tooltipExtra={earlyTooltipDescription}
          isMobile={isMobile}
          icon={<EarlyArrivalIcon className={styles.labelIcon} />}
        />
      )}
      {showLateDepartureWarning && (
        <LabelWithDescription
          text={i18n.t('checkout.flight.late_departure.title')}
          tooltipTitle={lateTooltipTitle}
          tooltipExtra={lateTooltipDescription}
          isMobile={isMobile}
          icon={<LateDepartureIcon className={styles.labelIcon} />}
        />
      )}
    </div>
  );
};
